<script setup lang="ts">
import { inject, ref, Ref, onMounted, reactive, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue-sonner'
import { sendGraphQLQuery } from '@/services/gql-util'
import MediaList from '@/components/MediaList.vue'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import {
	MapPinIcon, PencilSquareIcon,
	UserPlusIcon, TrashIcon, FolderIcon,
	EnvelopeIcon, PhoneIcon,
	CalendarIcon, ClockIcon, DocumentTextIcon
} from '@heroicons/vue/24/solid'
import { Member } from '@/types/Member'
import { Media } from '@/types/Media'
import { Filters } from '@/types/Filters'
import { updateUser, getUserInfo as gqlGetUserInfo, getUserActivity } from '@/services/User'
import { useGroups } from '@/services/Groups'

const vhostId = inject<Ref<string>>('vhostId', ref(''))
const loggedInUser = inject<Ref<Member> | null>('userInfo', null)
const mmMode = inject<Ref<boolean>>('mmMode', ref(false))
const { getUserGroups: gqlGetUserGroups, getGroups: gqlGetGroups, addUserToGroup: gqlAddUserToGroup, removeUserFromGroup: gqlRemoveUserFromGroup } = useGroups()
const route = useRoute()
const router = useRouter()
const userId = ref(route.params.id as string)
const memberInfo = ref<Member | null>(null)
const breadcrumb = inject<Ref<string>>('breadcrumb', ref(''))
const filters = reactive<Filters>({ filetype: [1, 2], status: [3], startTime: '30 days ago', moderationStatus: '0' })
const selectedMedia = ref<Media[]>([])
const tab = ref('overview')
const loading = ref(true)

// Edit state
const editing = ref(false)
const saving = ref(false)
const editForm = ref({
	firstname: '',
	lastname: '',
	email: '',
	phone: '',
	city: '',
	state: '',
	country: '',
	postalcode: '',
	address1: '',
	address2: '',
	description: '',
	website: '',
	active: '',
	disabled: '',
})

// Groups
const userGroups = ref<{ id: number; name: string }[]>([])
const allGroups = ref<{ id: string; name: string }[]>([])
const showAddGroupDialog = ref(false)
const selectedGroupToAdd = ref('')
const addingGroup = ref(false)

// Metadata
const userMeta = ref<Record<string, any>>({})
const userMetaFieldDefs = ref<{ label: string; key: string; type: string }[]>([])
const editingMeta = ref(false)
const savingMeta = ref(false)
const metaForm = ref<Record<string, string>>({})

// Activity
const activityLog = ref<any[]>([])
const activityLoading = ref(false)

// Computed
const isAdmin = computed(() => {
	if (!loggedInUser?.value?.permissions || !vhostId.value) return false
	const perms = loggedInUser.value.permissions[vhostId.value]
	return perms?.includes('users.updateUserInfo') || perms?.includes('admin')
})

const statusBadge = computed(() => {
	if (!memberInfo.value) return null
	if (memberInfo.value.disabled === '1') return { label: 'Disabled', class: 'bg-red-500/15 text-red-700 border-red-500/30' }
	if (memberInfo.value.active === '1') return { label: 'Active', class: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' }
	return { label: 'Inactive', class: 'bg-amber-500/15 text-amber-700 border-amber-500/30' }
})

const totalUploads = ref(0)
const setTotalCount = (count: number) => { totalUploads.value = count }

const memberRole = computed(() => {
	if (!memberInfo.value) return ''
	const perms = memberInfo.value.permissions?.[vhostId.value]
	if (perms?.includes('admin')) return 'Super Admin'
	if (perms?.includes('users.updateUserInfo')) return 'Admin'
	if (perms?.includes('moderation.moderate')) return 'Moderator'
	return 'Member'
})

const formatDate = (dateStr?: string) => {
	if (!dateStr) return '—'
	try {
		const d = new Date(dateStr)
		return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
	} catch { return dateStr }
}

// API calls
const fetchUserInfo = async () => {
	loading.value = true
	try {
		const result = await gqlGetUserInfo(Number(userId.value), Number(vhostId.value))
		memberInfo.value = result
		if (memberInfo.value) {
			breadcrumb.value = memberInfo.value.firstname || memberInfo.value.user || 'User'
			userMeta.value = memberInfo.value.meta || {}
		}
	} catch (err) {
		console.error('Error fetching user info:', err)
	} finally {
		loading.value = false
	}
}

const fetchUserGroups = async () => {
	try {
		userGroups.value = await gqlGetUserGroups(Number(userId.value), Number(vhostId.value))
	} catch (err) {
		console.error('Error fetching user groups:', err)
		userGroups.value = []
	}
}

const fetchAllGroups = async () => {
	try {
		const groups = await gqlGetGroups(Number(vhostId.value))
		allGroups.value = groups.map((g: any) => ({ id: g.id, name: g.name }))
	} catch (err) {
		console.error('Error fetching groups:', err)
	}
}

const fetchUserMetaFieldDefs = async () => {
	try {
		const query = `
		query ($vhost: Int!, $category: String!, $locale: String) {
			I18n {
				strings(vhost: $vhost, category: $category, locale: $locale) {
					key value
				}
			}
		}`;
		const data = await sendGraphQLQuery<{ I18n: { strings: Array<{ key: string; value: string }> } }>(query, {
			vhost: Number(vhostId.value),
			category: 'registration',
			locale: 'en',
		})
		const userFieldsEntry = data?.I18n?.strings?.find((s: any) => s.key === 'userFields')
		if (userFieldsEntry?.value) {
			const fields = JSON.parse(userFieldsEntry.value)
			userMetaFieldDefs.value = (fields || []).map((f: any) => ({
				label: f.label || f.field || f.key || '',
				key: f.key || f.field || f.label || '',
				type: f.type || 'Text',
			}))
		}
	} catch (err) {
		// If we can't fetch field defs, we'll just show raw meta keys
		console.error('Error fetching user meta field definitions:', err)
	}
}

const fetchActivity = async () => {
	activityLoading.value = true
	try {
		activityLog.value = await getUserActivity(Number(userId.value), Number(vhostId.value))
	} catch (err) {
		console.error('Error fetching activity:', err)
		activityLog.value = []
	} finally {
		activityLoading.value = false
	}
}

// Edit actions
const startEditing = () => {
	if (!memberInfo.value) return
	editForm.value = {
		firstname: memberInfo.value.firstname || '',
		lastname: memberInfo.value.lastname || '',
		email: memberInfo.value.email || '',
		phone: memberInfo.value.phone || '',
		city: memberInfo.value.city || '',
		state: memberInfo.value.state || '',
		country: memberInfo.value.country || '',
		postalcode: memberInfo.value.postalcode || '',
		address1: memberInfo.value.address1 || '',
		address2: memberInfo.value.address2 || '',
		description: memberInfo.value.description || '',
		website: memberInfo.value.website || '',
		active: memberInfo.value.active || '0',
		disabled: memberInfo.value.disabled || '0',
	}
	editing.value = true
}

const saveUser = async () => {
	if (!memberInfo.value) return
	saving.value = true
	try {
		const updated = await updateUser(memberInfo.value.id, editForm.value)
		if (updated) {
			Object.assign(memberInfo.value, updated)
		}
		editing.value = false
		toast.success('User updated successfully')
	} catch (err) {
		console.error('Failed to update user:', err)
		toast.error('Failed to update user')
	} finally {
		saving.value = false
	}
}

const toggleUserStatus = async (field: 'active' | 'disabled', value: boolean) => {
	if (!memberInfo.value) return
	try {
		await updateUser(memberInfo.value.id, { [field]: value ? '1' : '0' })
		memberInfo.value[field] = value ? '1' : '0'
		toast.success(`User ${field === 'active' ? (value ? 'activated' : 'deactivated') : (value ? 'disabled' : 'enabled')}`)
	} catch (err) {
		toast.error('Failed to update user status')
	}
}

// Group actions
const addUserToGroup = async () => {
	if (!selectedGroupToAdd.value) return
	addingGroup.value = true
	try {
		await gqlAddUserToGroup(Number(selectedGroupToAdd.value), Number(userId.value))
		await fetchUserGroups()
		showAddGroupDialog.value = false
		selectedGroupToAdd.value = ''
		toast.success('User added to group')
	} catch (err) {
		toast.error('Failed to add user to group')
	} finally {
		addingGroup.value = false
	}
}

const removeUserFromGroup = async (groupId: number) => {
	try {
		await gqlRemoveUserFromGroup(groupId, Number(userId.value))
		await fetchUserGroups()
		toast.success('User removed from group')
	} catch (err) {
		toast.error('Failed to remove user from group')
	}
}

// Meta actions
const startEditingMeta = () => {
	metaForm.value = { ...userMeta.value }
	editingMeta.value = true
}

const saveMeta = async () => {
	if (!memberInfo.value) return
	savingMeta.value = true
	try {
		await updateUser(memberInfo.value.id, { meta: metaForm.value } as any)
		userMeta.value = { ...metaForm.value }
		if (memberInfo.value) memberInfo.value.meta = { ...metaForm.value }
		editingMeta.value = false
		toast.success('Metadata saved')
	} catch (err) {
		toast.error('Failed to save metadata')
	} finally {
		savingMeta.value = false
	}
}

// Watch for route changes
watch(() => route.params.id, (newId) => {
	if (newId && newId !== userId.value) {
		userId.value = newId as string
		fetchUserInfo()
		fetchUserGroups()
		fetchActivity()
	}
})

onMounted(async () => {
	if (mmMode.value) tab.value = 'uploads'
	await fetchUserInfo()
	fetchUserGroups()
	fetchAllGroups()
	fetchUserMetaFieldDefs()
	fetchActivity()
})
</script>

<template>
	<!-- ═══════════════════════════════════════════════════════ -->
	<!-- mmMode: Simplified Member Layout                       -->
	<!-- ═══════════════════════════════════════════════════════ -->
	<div v-if="!loading && memberInfo && mmMode" class="flex h-full">
		<!-- Main content -->
		<div class="flex-1 min-w-0 p-4 lg:p-6 space-y-5 overflow-y-auto">
			<!-- Header -->
			<div class="flex items-start justify-between gap-4">
				<div class="flex items-center gap-4 min-w-0">
					<img :src="'/services/useravatar/' + memberInfo.id + '/small'" class="w-16 h-16 rounded-full object-cover border-2 border-muted shrink-0" />
					<div class="min-w-0">
						<div class="flex items-center gap-2 flex-wrap">
							<h1 class="text-xl font-semibold truncate">{{ memberInfo.firstname }} {{ memberInfo.lastname }}</h1>
							<Badge v-if="statusBadge" :class="statusBadge.class" variant="outline" class="text-xs">{{ statusBadge.label }}</Badge>
							<Badge v-if="memberRole" variant="secondary" class="text-xs">{{ memberRole }}</Badge>
						</div>
						<div class="flex items-center gap-4 mt-1.5 text-sm text-muted-foreground flex-wrap">
							<span v-if="memberInfo.email" class="flex items-center gap-1">
								<EnvelopeIcon class="h-3.5 w-3.5" /> {{ memberInfo.email }}
							</span>
							<span v-if="memberInfo.description" class="flex items-center gap-1">
								Company: {{ memberInfo.description }}
							</span>
							<span v-if="memberInfo.country" class="flex items-center gap-1">
								<MapPinIcon class="h-3.5 w-3.5" /> {{ memberInfo.country }}
							</span>
							<span v-if="memberInfo.lastlogin" class="flex items-center gap-1">
								<ClockIcon class="h-3.5 w-3.5" /> Last seen {{ formatDate(memberInfo.lastlogin) }}
							</span>
						</div>
					</div>
				</div>
				<Button v-if="isAdmin" size="sm" @click="startEditing" class="gap-1.5 shrink-0">
					<PencilSquareIcon class="h-4 w-4" /> Edit
				</Button>
			</div>

			<!-- Tabs: Assets + Activities only -->
			<Tabs v-model="tab" default-value="uploads">
				<TabsList>
					<TabsTrigger value="uploads">Assets</TabsTrigger>
					<TabsTrigger value="activity">Activities</TabsTrigger>
				</TabsList>

				<!-- Assets Tab -->
				<TabsContent value="uploads" class="mt-4 space-y-3">
					<div class="flex items-center justify-between">
						<p class="text-sm text-muted-foreground">{{ totalUploads }} Upload{{ totalUploads !== 1 ? 's' : '' }}</p>
					</div>
					<MediaList v-model="selectedMedia" v-if="vhostId" :filters="{ ...filters, uid: Number(userId), moderationStatus: '-1' }" type="grid" :vhostId="vhostId" sort-order="upload DESC" default-profile="11" @set-total-count="setTotalCount" />
				</TabsContent>

				<!-- Activities Tab -->
				<TabsContent value="activity" class="mt-4">
					<div v-if="activityLog.length > 0" class="border rounded-md divide-y">
						<div v-for="(entry, idx) in activityLog" :key="idx" class="flex items-start gap-3 p-3">
							<div class="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
								<ClockIcon class="h-4 w-4 text-muted-foreground" />
							</div>
							<div class="min-w-0 flex-1">
								<p class="text-sm">{{ entry.action || entry.description || entry.type || 'Activity' }}</p>
								<p class="text-xs text-muted-foreground">{{ formatDate(entry.created || entry.date || entry.timestamp) }}</p>
							</div>
						</div>
					</div>
					<div v-else class="text-center py-12 text-muted-foreground">
						<ClockIcon class="h-10 w-10 mx-auto opacity-30 mb-2" />
						<p class="text-sm">{{ activityLoading ? 'Loading activity...' : 'No activity recorded.' }}</p>
					</div>
				</TabsContent>
			</Tabs>
		</div>

		<!-- Right sidebar: Folder Membership -->
		<div class="w-[260px] shrink-0 border-l bg-background p-4 space-y-4 overflow-y-auto">
			<div class="flex items-center justify-between">
				<h3 class="text-sm font-medium">Folder Membership</h3>
				<Dialog v-model:open="showAddGroupDialog" v-if="isAdmin">
					<DialogTrigger as-child>
						<Button variant="ghost" size="sm" class="h-7 w-7 p-0">
							<UserPlusIcon class="h-4 w-4" />
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Add to Folder</DialogTitle>
						</DialogHeader>
						<div class="py-4">
							<Select v-model="selectedGroupToAdd">
								<SelectTrigger>
									<SelectValue placeholder="Select a folder" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem v-for="group in allGroups" :key="group.id" :value="group.id">{{ group.name }}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<DialogFooter>
							<Button variant="outline" @click="showAddGroupDialog = false">Cancel</Button>
							<Button :disabled="!selectedGroupToAdd || addingGroup" @click="addUserToGroup">
								{{ addingGroup ? 'Adding...' : 'Add' }}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
			<div v-if="userGroups.length > 0" class="space-y-1">
				<div v-for="group in userGroups" :key="group.id" class="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 group">
					<div class="flex items-center gap-2 text-sm min-w-0">
						<FolderIcon class="h-3.5 w-3.5 text-muted-foreground shrink-0" />
						<span class="truncate">{{ group.name }}</span>
					</div>
					<Button v-if="isAdmin" variant="ghost" size="sm" class="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive" @click="removeUserFromGroup(group.id)">
						<TrashIcon class="h-3.5 w-3.5" />
					</Button>
				</div>
			</div>
			<div v-else class="text-center py-8 text-muted-foreground">
				<FolderIcon class="h-8 w-8 mx-auto opacity-20 mb-2" />
				<p class="text-xs">No folders found</p>
			</div>
		</div>
	</div>

	<!-- ═══════════════════════════════════════════════════════ -->
	<!-- Standard (UGC) Mode: Full Member Layout                -->
	<!-- ═══════════════════════════════════════════════════════ -->
	<div class="w-full p-4 lg:p-6 space-y-6" v-else-if="!loading && memberInfo">
		<!-- Header -->
		<div class="flex flex-col lg:flex-row gap-6 items-start">
			<div class="flex items-start gap-5 flex-1 min-w-0">
				<img :src="'/services/useravatar/' + memberInfo.id + '/small'" class="w-20 h-20 rounded-full object-cover border-2 border-muted shrink-0" />
				<div class="min-w-0 flex-1">
					<div class="flex items-center gap-3 flex-wrap">
						<h1 class="text-2xl font-semibold truncate">{{ memberInfo.firstname }} {{ memberInfo.lastname }}</h1>
						<Badge v-if="statusBadge" :class="statusBadge.class" variant="outline" class="text-xs">{{ statusBadge.label }}</Badge>
					</div>
					<p class="text-sm text-muted-foreground mt-0.5">{{ memberInfo.user }}</p>
					<div class="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
						<span v-if="memberInfo.email" class="flex items-center gap-1">
							<EnvelopeIcon class="h-3.5 w-3.5" /> {{ memberInfo.email }}
						</span>
						<span v-if="memberInfo.phone" class="flex items-center gap-1">
							<PhoneIcon class="h-3.5 w-3.5" /> {{ memberInfo.phone }}
						</span>
						<span v-if="memberInfo.city || memberInfo.state" class="flex items-center gap-1">
							<MapPinIcon class="h-3.5 w-3.5" /> {{ [memberInfo.city, memberInfo.state, memberInfo.country].filter(Boolean).join(', ') }}
						</span>
					</div>
					<div class="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
						<span v-if="memberInfo.created" class="flex items-center gap-1">
							<CalendarIcon class="h-3 w-3" /> Joined {{ formatDate(memberInfo.created) }}
						</span>
						<span v-if="memberInfo.lastlogin" class="flex items-center gap-1">
							<ClockIcon class="h-3 w-3" /> Last login {{ formatDate(memberInfo.lastlogin) }}
						</span>
					</div>
				</div>
			</div>

			<div class="flex items-center gap-3 shrink-0">
				<div class="grid grid-cols-3 gap-3 text-center">
					<div class="px-4 py-2 rounded-lg bg-muted/50">
						<p class="text-lg font-semibold">{{ memberInfo.publicfiles || 0 }}</p>
						<p class="text-xs text-muted-foreground">Approved</p>
					</div>
					<div class="px-4 py-2 rounded-lg bg-muted/50">
						<p class="text-lg font-semibold">{{ memberInfo.unmoderatedfiles || 0 }}</p>
						<p class="text-xs text-muted-foreground">Pending</p>
					</div>
					<div class="px-4 py-2 rounded-lg bg-muted/50">
						<p class="text-lg font-semibold">{{ memberInfo.deniedfiles || 0 }}</p>
						<p class="text-xs text-muted-foreground">Denied</p>
					</div>
				</div>
				<Button v-if="isAdmin" variant="outline" size="sm" @click="startEditing" class="gap-1.5">
					<PencilSquareIcon class="h-4 w-4" /> Edit
				</Button>
			</div>
		</div>

		<!-- Tabs -->
		<Tabs v-model="tab" default-value="overview">
			<TabsList>
				<TabsTrigger value="overview">Overview</TabsTrigger>
				<TabsTrigger value="uploads">Uploads</TabsTrigger>
				<TabsTrigger value="groups">Groups</TabsTrigger>
				<TabsTrigger value="metadata">Metadata</TabsTrigger>
				<TabsTrigger value="activity">Activity</TabsTrigger>
			</TabsList>

			<!-- Overview Tab -->
			<TabsContent value="overview" class="space-y-6 mt-4">
				<div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<Card class="lg:col-span-1">
						<CardHeader class="pb-3">
							<CardTitle class="text-sm font-medium">User Details</CardTitle>
						</CardHeader>
						<CardContent class="space-y-3 text-sm">
							<div v-if="memberInfo.email">
								<p class="text-xs text-muted-foreground">Email</p>
								<a :href="'mailto:' + memberInfo.email" class="text-primary hover:underline">{{ memberInfo.email }}</a>
							</div>
							<div v-if="memberInfo.phone">
								<p class="text-xs text-muted-foreground">Phone</p>
								<p>{{ memberInfo.phone }}</p>
							</div>
							<div v-if="memberInfo.address1">
								<p class="text-xs text-muted-foreground">Address</p>
								<p>{{ memberInfo.address1 }}</p>
								<p v-if="memberInfo.address2">{{ memberInfo.address2 }}</p>
								<p>{{ [memberInfo.city, memberInfo.state, memberInfo.postalcode].filter(Boolean).join(', ') }}</p>
								<p v-if="memberInfo.country">{{ memberInfo.country }}</p>
							</div>
							<div v-if="memberInfo.website">
								<p class="text-xs text-muted-foreground">Website</p>
								<a :href="memberInfo.website" target="_blank" class="text-primary hover:underline">{{ memberInfo.website }}</a>
							</div>
							<div v-if="memberInfo.birthdate">
								<p class="text-xs text-muted-foreground">Birthdate</p>
								<p>{{ formatDate(memberInfo.birthdate) }}</p>
							</div>
							<div v-if="memberInfo.description">
								<p class="text-xs text-muted-foreground">Bio</p>
								<p class="text-sm">{{ memberInfo.description }}</p>
							</div>

							<Separator v-if="isAdmin" />

							<div v-if="isAdmin" class="space-y-3">
								<div class="flex items-center justify-between">
									<Label class="text-sm">Active</Label>
									<Switch :checked="memberInfo.active === '1'" @update:checked="(val: boolean) => toggleUserStatus('active', val)" />
								</div>
								<div class="flex items-center justify-between">
									<Label class="text-sm">Disabled</Label>
									<Switch :checked="memberInfo.disabled === '1'" @update:checked="(val: boolean) => toggleUserStatus('disabled', val)" />
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="lg:col-span-2">
						<CardHeader class="pb-3">
							<div class="flex items-center justify-between">
								<CardTitle class="text-sm font-medium">Recent Uploads</CardTitle>
								<Button variant="ghost" size="sm" @click="tab = 'uploads'" class="text-xs">View All</Button>
							</div>
						</CardHeader>
						<CardContent>
							<MediaList v-model="selectedMedia" v-if="vhostId" :filters="{ ...filters, uid: Number(userId) }" type="grid" :vhostId="vhostId" sort-order="upload DESC" default-profile="11" />
						</CardContent>
					</Card>
				</div>

				<Card v-if="userGroups.length > 0">
					<CardHeader class="pb-3">
						<div class="flex items-center justify-between">
							<CardTitle class="text-sm font-medium">Group Memberships</CardTitle>
							<Button variant="ghost" size="sm" @click="tab = 'groups'" class="text-xs">Manage</Button>
						</div>
					</CardHeader>
					<CardContent>
						<div class="flex flex-wrap gap-2">
							<Badge v-for="group in userGroups" :key="group.id" variant="secondary" class="gap-1">
								<FolderIcon class="h-3 w-3" /> {{ group.name }}
							</Badge>
						</div>
					</CardContent>
				</Card>
			</TabsContent>

			<!-- Uploads Tab -->
			<TabsContent value="uploads" class="mt-4">
				<MediaList v-model="selectedMedia" v-if="vhostId" :filters="{ ...filters, uid: Number(userId) }" type="grid" :vhostId="vhostId" sort-order="upload DESC" default-profile="11" />
			</TabsContent>

			<!-- Groups Tab -->
			<TabsContent value="groups" class="mt-4 space-y-4">
				<div class="flex items-center justify-between">
					<p class="text-sm text-muted-foreground">{{ userGroups.length }} group{{ userGroups.length !== 1 ? 's' : '' }}</p>
					<Dialog v-model:open="showAddGroupDialog" v-if="isAdmin">
						<DialogTrigger as-child>
							<Button size="sm" class="gap-1.5"><UserPlusIcon class="h-4 w-4" /> Add to Group</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Add User to Group</DialogTitle>
							</DialogHeader>
							<div class="py-4">
								<Select v-model="selectedGroupToAdd">
									<SelectTrigger>
										<SelectValue placeholder="Select a group" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem v-for="group in allGroups" :key="group.id" :value="group.id">{{ group.name }}</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<DialogFooter>
								<Button variant="outline" @click="showAddGroupDialog = false">Cancel</Button>
								<Button :disabled="!selectedGroupToAdd || addingGroup" @click="addUserToGroup">
									{{ addingGroup ? 'Adding...' : 'Add' }}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>
				<div class="border rounded-md" v-if="userGroups.length > 0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Group Name</TableHead>
								<TableHead>ID</TableHead>
								<TableHead v-if="isAdmin" class="w-[80px]"></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow v-for="group in userGroups" :key="group.id">
								<TableCell class="font-medium">
									<div class="flex items-center gap-2">
										<FolderIcon class="h-4 w-4 text-muted-foreground" />
										{{ group.name }}
									</div>
								</TableCell>
								<TableCell class="text-muted-foreground">{{ group.id }}</TableCell>
								<TableCell v-if="isAdmin">
									<Button variant="ghost" size="sm" class="h-8 w-8 p-0 text-destructive hover:text-destructive" @click="removeUserFromGroup(group.id)">
										<TrashIcon class="h-4 w-4" />
									</Button>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</div>
				<div v-else class="text-center py-12 text-muted-foreground">
					<FolderIcon class="h-10 w-10 mx-auto opacity-30 mb-2" />
					<p class="text-sm">This user is not in any groups.</p>
				</div>
			</TabsContent>

			<!-- Metadata Tab -->
			<TabsContent value="metadata" class="mt-4 space-y-4">
				<div class="flex items-center justify-between">
					<p class="text-sm text-muted-foreground">User metadata</p>
					<Button v-if="isAdmin && !editingMeta" size="sm" variant="outline" @click="startEditingMeta" class="gap-1.5">
						<PencilSquareIcon class="h-4 w-4" /> Edit
					</Button>
					<div v-if="editingMeta" class="flex gap-2">
						<Button size="sm" variant="outline" @click="editingMeta = false">Cancel</Button>
						<Button size="sm" :disabled="savingMeta" @click="saveMeta">{{ savingMeta ? 'Saving...' : 'Save' }}</Button>
					</div>
				</div>

				<!-- Show meta using field definitions if available -->
				<div v-if="Object.keys(userMeta).length > 0 || (editingMeta && userMetaFieldDefs.length > 0)" class="border rounded-md">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead class="w-[200px]">Field</TableHead>
								<TableHead>Value</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<!-- Show defined fields first (from uploader config) -->
							<template v-if="userMetaFieldDefs.length > 0">
								<TableRow v-for="field in userMetaFieldDefs" :key="field.key">
									<TableCell class="font-medium text-sm">{{ field.label }}</TableCell>
									<TableCell>
										<template v-if="editingMeta">
											<Textarea v-if="field.type === 'Text Area'" v-model="metaForm[field.key]" rows="2" class="max-w-md" />
											<div v-else-if="field.type === 'Checkbox'" class="flex items-center gap-2">
												<input type="checkbox" :checked="metaForm[field.key] === '1' || metaForm[field.key] === 'true'" @change="metaForm[field.key] = ($event.target as HTMLInputElement).checked ? '1' : '0'" class="rounded border-input" />
											</div>
											<Input v-else v-model="metaForm[field.key]" class="h-8 max-w-xs" :type="field.type === 'Email' ? 'email' : field.type === 'Number' ? 'number' : 'text'" />
										</template>
										<template v-else>
											<span class="text-sm">{{ userMeta[field.key] || '—' }}</span>
										</template>
									</TableCell>
								</TableRow>
							</template>
							<!-- Show any remaining meta keys not covered by field defs -->
							<template v-for="(value, key) in userMeta" :key="key">
								<TableRow v-if="!userMetaFieldDefs.some(f => f.key === key) && key !== 'dashboard'">
									<TableCell class="font-medium text-sm capitalize">{{ String(key).replace(/_/g, ' ') }}</TableCell>
									<TableCell>
										<template v-if="editingMeta">
											<Input v-model="metaForm[key as string]" class="h-8 max-w-xs" />
										</template>
										<template v-else>
											<span class="text-sm">{{ typeof value === 'object' ? JSON.stringify(value) : value || '—' }}</span>
										</template>
									</TableCell>
								</TableRow>
							</template>
						</TableBody>
					</Table>
				</div>

				<div v-else class="text-center py-12 text-muted-foreground">
					<DocumentTextIcon class="h-10 w-10 mx-auto opacity-30 mb-2" />
					<p class="text-sm">No metadata for this user.</p>
					<Button v-if="isAdmin" size="sm" variant="outline" class="mt-3" @click="startEditingMeta">Add Metadata</Button>
				</div>
			</TabsContent>

			<!-- Activity Tab -->
			<TabsContent value="activity" class="mt-4">
				<div v-if="activityLog.length > 0" class="border rounded-md divide-y">
					<div v-for="(entry, idx) in activityLog" :key="idx" class="flex items-start gap-3 p-3">
						<div class="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
							<ClockIcon class="h-4 w-4 text-muted-foreground" />
						</div>
						<div class="min-w-0 flex-1">
							<p class="text-sm">{{ entry.action || entry.description || entry.type || 'Activity' }}</p>
							<p class="text-xs text-muted-foreground">{{ formatDate(entry.created || entry.date || entry.timestamp) }}</p>
						</div>
					</div>
				</div>
				<div v-else class="text-center py-12 text-muted-foreground">
					<ClockIcon class="h-10 w-10 mx-auto opacity-30 mb-2" />
					<p class="text-sm">{{ activityLoading ? 'Loading activity...' : 'No activity recorded.' }}</p>
				</div>
			</TabsContent>
		</Tabs>

		<!-- Edit User Dialog -->
		<Dialog v-model:open="editing">
			<DialogContent class="max-w-2xl max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Edit User — {{ memberInfo.firstname }} {{ memberInfo.lastname }}</DialogTitle>
				</DialogHeader>
				<div class="grid grid-cols-2 gap-4 py-4">
					<div class="space-y-1">
						<Label class="text-xs">First Name</Label>
						<Input v-model="editForm.firstname" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Last Name</Label>
						<Input v-model="editForm.lastname" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Email</Label>
						<Input v-model="editForm.email" type="email" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Phone</Label>
						<Input v-model="editForm.phone" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Website</Label>
						<Input v-model="editForm.website" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Address Line 1</Label>
						<Input v-model="editForm.address1" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Address Line 2</Label>
						<Input v-model="editForm.address2" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">City</Label>
						<Input v-model="editForm.city" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">State / Province</Label>
						<Input v-model="editForm.state" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Postal Code</Label>
						<Input v-model="editForm.postalcode" />
					</div>
					<div class="space-y-1">
						<Label class="text-xs">Country</Label>
						<Input v-model="editForm.country" />
					</div>
					<div class="space-y-1 col-span-2">
						<Label class="text-xs">Bio</Label>
						<Textarea v-model="editForm.description" rows="3" />
					</div>

					<Separator class="col-span-2" />

					<div class="flex items-center justify-between col-span-1">
						<Label>Active</Label>
						<Switch :checked="editForm.active === '1'" @update:checked="(val: boolean) => editForm.active = val ? '1' : '0'" />
					</div>
					<div class="flex items-center justify-between col-span-1">
						<Label>Disabled</Label>
						<Switch :checked="editForm.disabled === '1'" @update:checked="(val: boolean) => editForm.disabled = val ? '1' : '0'" />
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" @click="editing = false">Cancel</Button>
					<Button :disabled="saving" @click="saveUser">{{ saving ? 'Saving...' : 'Save Changes' }}</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>

	<!-- Loading state -->
	<div v-else-if="loading" class="flex items-center justify-center h-64">
		<p class="text-muted-foreground">Loading user...</p>
	</div>
</template>
